// apps/client/src/world/outdoor.ts (M0-T13) — the outdoor town scene: terrain, water, decor, FX and every world object, within the
// draw-call budget (§8.4). Features attach their visuals to named layers instead of editing this file. M0-T18's `spaces/out.ts` wraps it.
import { getEntry, tuning } from '@whiskerton/content';
import { groundAt, rotateFootprint, tierAt, type BuildingState, type HouseState, type MapData, type ObjectId, type PrivateState, type TileXY, type Vec2,
  type WorldObject } from '@whiskerton/shared';
import { heightAt as tierHeight } from '@whiskerton/shared/map/nav';   // subpath-only (not in the barrel)
import * as THREE from 'three';
import { blobMaterial } from '../assets/blob';
import { batchStatic } from '../assets/loader';
import { IS_DEV } from '../boot/env';
import type { LightValues } from '../engine/lighting';
import { fadeAt } from '../engine/shader/fade';
import { ghostMaterials, neutralGround, patchObject, worldUniforms } from '../engine/shader/worldPatch';
import { buildDecor } from './decor';
import { createFx } from './fx';
import { ghostMesh, GHOST_COLOR_ORDER, GHOST_DEPTH_ORDER, Instancer } from './instancer';
import { fadeCylinder, viewBounds } from './objectBounds';
import { objectViewModules } from './objects.gen';
import { InstanceProxy, placeOnTile, type ObjectView, type ViewCtx } from './objectView';
import { buildBeach } from './terrain/beach';
import { beachHeight, SEA_LEVEL, waterSurface } from './terrain/field';
import { GroundLayer, type GroundMarks, type GroundWorn } from './terrain/ground';
import { buildRamps } from './terrain/ramps';
import { buildTerraces } from './terrain/terraces';
import { buildWater } from './terrain/water';

export interface OutdoorScene {
  readonly scene: THREE.Scene;
  build(map: MapData, objects: readonly WorldObject[], ctx: ViewCtx): Promise<void>;
  addObject(o: WorldObject): Promise<void>; updateObject(o: WorldObject): void; removeObject(id: ObjectId): void;
  layer(name: string): THREE.Group;            // e.g. 'items' (M1-T13), 'fishing' (M1-T15), 'digging' (M2-T09), 'villagers' (M2-T11)
  heightAt(p: Vec2): number;
  refreshPerResident(you: PrivateState): void; // re-runs ObjectView.update for perResident views
  tick(dt: number, light: LightValues): void;  // rev 7: water, smoke, butterflies, sparkles; water tint from the clock lighting
  readonly picking: { objectAt(tile: TileXY): WorldObject | null };   // rev 7: tile → object index for tap picking
  /** Title screen (2026-10-05): frees what the builds made (never kit geometry or materials, which the asset cache keeps for the next
   *  town) and empties the scene; a build still loading stops. The scene is not used again. */
  dispose(): void;
}

const FADE = tuning.light.fade;
/** The world shader clips any object whose origin lies beyond the horizon (contract change 13), so the map-wide meshes (terrain, water,
 *  the static batch) keep their local origin within this distance of the camera target: past it, their geometry is rebased. */
const REBASE_AT = 20;
/** batchStatic bakes world matrices into the vertices, which quantized kit attributes (meshopt's normalized Int16) cannot hold: those
 *  meshes get a float copy of their geometry first (one per source geometry, shared by every clone). */
const floatCopies = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();
type Attr = THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
const COMPONENT = [(a: Attr, i: number) => a.getX(i), (a: Attr, i: number) => a.getY(i), (a: Attr, i: number) => a.getZ(i), (a: Attr, i: number) => a.getW(i)];
function floatGeometry(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const attrs = Object.entries(g.attributes) as [string, Attr][];
  if (attrs.every(([, a]) => !(a as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute && a.array instanceof Float32Array && !a.normalized)) return g;
  let out = floatCopies.get(g);
  if (out) return out;
  out = new THREE.BufferGeometry();
  for (const [name, a] of attrs) {
    const arr = new Float32Array(a.count * a.itemSize);
    for (let i = 0; i < a.count; i++) for (let k = 0; k < a.itemSize; k++) arr[i * a.itemSize + k] = COMPONENT[k]!(a, i);   // getX… denormalize
    out.setAttribute(name, new THREE.BufferAttribute(arr, a.itemSize));
  }
  if (g.index) out.setIndex(g.index);
  for (const grp of g.groups) out.addGroup(grp.start, grp.count, grp.materialIndex);
  floatCopies.set(g, out);
  return out;
}

const VIEWS = objectViewModules as readonly { view: ObjectView }[];
const LAMP = tuning.light.ground.lamp, SUN_FULL = tuning.light.ground.sun.fullAt;
/** What the objects leave on the ground map: canopies, rocks, building footprints, and the lamp at every lived-in door and the shop's
 *  and museum's (light and look, 2026-10-16). The plaza's well, a round ring under a small roof on posts, marks like a canopy: as a
 *  footprint its roof's box left a square of shade round it. */
export function groundMarksOf(map: MapData, entries: Iterable<{ obj: WorldObject; view: THREE.Object3D }>): GroundMarks {
  const trees: GroundMarks['trees'][number][] = [], stones: GroundMarks['stones'][number][] = [], blocks: GroundMarks['blocks'][number][] = [];
  const lamps: { x: number; z: number }[] = [], c = new THREE.Vector3();
  const lampAt = (door: TileXY | undefined) => { if (door) lamps.push({ x: door.x + 0.5, z: door.y + LAMP.inset }); };
  for (const { obj, view } of entries) {
    const k = obj.kind;
    const well = k === 'building' && (obj.state as BuildingState).role === 'well';
    if (k === 'tree' || k === 'rock' || k === 'stump' || well) {
      const b = viewBounds(view);
      if (b.isEmpty()) continue;
      b.getCenter(c);
      const r = Math.max(b.max.x - b.min.x, b.max.z - b.min.z) / 2;
      if (k === 'tree' || well) trees.push({ x: c.x, z: c.z, r, h: b.max.y - view.position.y });
      else stones.push({ x: c.x, z: c.z, r });
    } else if (k === 'house' || k === 'building' || k === 'dropbox') {
      const b = viewBounds(view);
      if (!b.isEmpty()) blocks.push({ x0: b.min.x, z0: b.min.z, x1: b.max.x, z1: b.max.z, h: b.max.y - view.position.y });
      if (k === 'house') { const h = obj.state as HouseState; if (h.occupant && !h.building) lampAt(map.plots[h.plot]?.door); }
      if (k === 'building') {
        const role = (obj.state as BuildingState).role;
        if (role === 'shop') lampAt(map.sites.shopDoor);
        if (role === 'museum') lampAt(map.sites.museumDoor);
      }
    }
  }
  return { trees, stones, blocks, lamps };
}
const viewFor = (o: WorldObject): ObjectView | undefined => VIEWS.find((m) => m.view.kinds.includes(o.kind))?.view;

/** `fade` (default true): objects fade whole when they hide a resident or crowd the camera, drawn by their translucent ghosts. The title's
 *  showcase town has no residents to follow and passes false. */
export function createOutdoorScene(opts: { fade?: boolean } = {}): OutdoorScene {
  const fading = opts.fade ?? true;
  const scene = new THREE.Scene();
  const instancedRoot = new THREE.Group(), staticSource = new THREE.Group(), staticBatch = new THREE.Group(), dynamic = new THREE.Group(), proxies = new THREE.Group();
  instancedRoot.name = 'instanced'; staticSource.name = 'staticSource'; staticBatch.name = 'static'; dynamic.name = 'dynamic'; proxies.name = 'proxies';
  const anchored = new THREE.Group(), anchor = new THREE.Vector2();   // map-wide meshes, in coordinates relative to anchor (x, z)
  anchored.name = 'anchored';
  staticSource.visible = false;
  /** The view a proxy belongs to (its top ancestor under `proxies`): every proxy of a tree, fruit included, fades with the tree. */
  const viewOf = (o: THREE.Object3D, under: THREE.Object3D) => { let v = o; while (v.parent && v.parent !== under) v = v.parent; return v; };
  const instancer = new Instancer(instancedRoot, fading ? (p, memo) => {
    const v = viewOf(p, proxies);
    let c = memo.get(v);
    if (c === undefined) memo.set(v, (c = fadeCylinder(v, FADE.minHeight)));
    return c;
  } : null);
  // Trees and rocks (2026-10-16): every render of the scene culls the instanced scenery to its camera and picks near or far models.
  scene.onBeforeRender = (renderer, _scene, camera) => instancer.view(camera, renderer.shadowMap.enabled);
  /** The static batch's ghosts: one pair per merged mesh, sharing its attributes, drawing just the fading views' vertices. */
  let staticGhosts: { geo: THREE.BufferGeometry; depth: THREE.Mesh; color: THREE.Mesh; ranges: Map<object, [number, number]> }[] = [];
  let staticFades: { view: THREE.Object3D; obj: readonly number[] }[] = [];
  const layers = new Map<string, THREE.Group>();
  const entries = new Map<ObjectId, { obj: WorldObject; view: THREE.Object3D; def: ObjectView }>();
  const tiles = new Map<number, ObjectId>();
  let map: MapData | null = null, ctx: ViewCtx | null = null, beach: ((x: number, z: number) => number) | null = null;
  let water: ReturnType<typeof buildWater> | null = null, fx: ReturnType<typeof createFx> | null = null, staticDirty = false;
  let terrain: THREE.Group | null = null, decor: THREE.Group | null = null;
  let ground: GroundLayer | null = null, groundDirty = false;
  const lampColour = new THREE.Color(LAMP.color);
  /** The occlusion fade, on the CPU: which instances and static views fade now, and their ghosts (engine/shader/fade.ts). */
  const fadeObjects = () => {
    const viewers = worldUniforms.uViewers.value, cam = worldUniforms.uFadeCam.value;
    instancer.fade(viewers, cam);
    const members = staticFades.filter((f) => fadeAt(f.obj, 0, viewers, cam, FADE.margin) > 0).map((f) => f.view);
    for (const g of staticGhosts) {
      let lo = Infinity, hi = -Infinity;
      for (const v of members) { const r = g.ranges.get(v); if (r) { lo = Math.min(lo, r[0]); hi = Math.max(hi, r[1]); } }
      const on = lo < hi;
      if (on) g.geo.setDrawRange(lo, hi - lo);
      g.depth.visible = g.color.visible = on;
    }
  };
  const paintGround = () => {
    groundDirty = false;
    if (ground && map) ground.mark(groundMarksOf(map, entries.values()));
  };

  const heightAt = (p: Vec2): number => {
    if (!map) return 0;
    const g = groundAt(map, Math.floor(p.x), Math.floor(p.y));
    return g === 'sand' || g === 'sea' ? beach!(p.x, p.y) : tierHeight(map, p);
  };
  const footprint = (o: WorldObject): number[] => {
    const e = getEntry(o.catalogId);
    const { w, d } = rotateFootprint(e && e.kind === 'world' ? e.footprint : [1, 1], o.rot);
    const out: number[] = [];
    for (let y = o.y; y < o.y + d; y++) for (let x = o.x; x < o.x + w; x++) out.push(y * 4096 + x);
    return out;
  };
  const fileView = (view: THREE.Object3D) => {
    let instanced = false;
    view.traverse((c) => { if (c instanceof InstanceProxy) { instancer.add(c); instanced = true; } });
    if (instanced) proxies.add(view);
    else if (view.userData.static) { staticSource.add(view); staticDirty = true; }
    else { patchObject(view, { wind: view.userData.wind }); dynamic.add(view); }
  };
  const unfileView = (view: THREE.Object3D) => {
    view.traverse((c) => { if (c instanceof InstanceProxy) instancer.remove(c); });
    if (view.parent === staticSource) staticDirty = true;
    view.removeFromParent();
  };
  const rebatch = () => {
    if (!staticDirty) return;
    staticDirty = false;
    staticBatch.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.dispose(); });
    staticBatch.clear();
    staticSource.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry = floatGeometry(m.geometry); });
    staticSource.visible = true;
    const cylinders = new Map<THREE.Object3D, [number, number, number, number]>();
    if (fading) for (const v of staticSource.children) { const c = fadeCylinder(v, FADE.minHeight); if (c) cylinders.set(v, c); }
    staticFades = [...cylinders].map(([view, obj]) => ({ view, obj }));
    const merged = batchStatic(staticSource, fading ? (m) => {
      if (m.userData.blob) return null;                          // a contact blob stays on the ground under a faded house
      const v = viewOf(m, staticSource), obj = cylinders.get(v);
      return obj ? { key: v, obj } : null;
    } : undefined);
    staticSource.visible = false;
    merged.traverse((o) => {                                     // the merged contact blobs never cast, nor occlude in the GTAO prepass
      if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).material === blobMaterial()) { o.castShadow = false; o.userData.blob = true; }
    });
    merged.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.translate(-anchor.x, 0, -anchor.y); });
    patchObject(merged);
    staticGhosts = [];
    for (const mesh of [...merged.children] as THREE.Mesh[]) {
      const ranges = mesh.userData.ranges as Map<object, [number, number]> | undefined;
      if (!ranges?.size || mesh.userData.blob) continue;
      const geo = new THREE.BufferGeometry();                     // the merged mesh's own attributes, with a draw range of its own
      for (const [name, attr] of Object.entries(mesh.geometry.attributes)) geo.setAttribute(name, attr);
      const mats = ghostMaterials(mesh.material as THREE.Material);
      const depth = new THREE.Mesh(geo, mats.depth), color = new THREE.Mesh(geo, mats.color);
      ghostMesh(depth, GHOST_DEPTH_ORDER); ghostMesh(color, GHOST_COLOR_ORDER);
      mesh.add(depth, color);                                     // disposed with it (rebatch, release)
      staticGhosts.push({ geo, depth, color, ranges });
    }
    staticBatch.add(merged);
    fx?.setChimneys(staticSource.children.flatMap((v) => {
      const c = v.userData.chimney as THREE.Vector3 | undefined;
      return c ? [v.localToWorld(c.clone())] : [];
    }));
  };
  /** View builds in flight, by object id. A build is filed only while its token is still the newest for that id: a later add or update
   *  (the newest state wins), a remove or a rebuild of the town supersedes it, and its view is dropped. */
  const pending = new Map<ObjectId, number>();
  let tokens = 0, epoch = 0;
  const add = async (obj: WorldObject, token = ++tokens) => {
    const def = viewFor(obj), c = ctx;
    pending.set(obj.id, token);
    let view: THREE.Object3D | null = null;
    if (def && c) {
      try { view = await def.build(obj, c); } catch (e) {        // one object that cannot load never blanks the town
        if (IS_DEV) console.warn(`outdoor: no view for ${obj.id} (${obj.catalogId})`, e);
      }
    }
    if (pending.get(obj.id) !== token) return;                   // superseded while loading
    pending.delete(obj.id);
    remove(obj.id);                                               // the view this one replaces (an update keeps it on screen until now)
    if (!view || !def) return;
    placeOnTile(view, obj, heightAt);
    if (map && obj.kind !== 'bridge') {                          // lilies and the like float on the water, bridges span it at bank height
      const tx = Math.floor(view.position.x), ty = Math.floor(view.position.z), g = groundAt(map, tx, ty);
      if (g === 'river' || g === 'pond') view.position.y = waterSurface(tierAt(map, tx, ty));
    }
    view.userData.objectId = obj.id;
    entries.set(obj.id, { obj, view, def });
    for (const t of footprint(obj)) tiles.set(t, obj.id);
    fileView(view);
    groundDirty = true;
  };
  const remove = (id: ObjectId) => {
    const e = entries.get(id);
    if (!e) return;
    for (const t of footprint(e.obj)) if (tiles.get(t) === id) tiles.delete(t);
    unfileView(e.view);
    entries.delete(id);
    groundDirty = true;
  };
  /** Frees what a previous build made for itself (never kit geometry or materials, nor the cached terrain textures). */
  const dispose = (root: THREE.Object3D, ownTextures = false) => root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh && !(o as THREE.Points).isPoints) return;
    if ((o as THREE.InstancedMesh).isInstancedMesh) (o as THREE.InstancedMesh).dispose();
    m.geometry.dispose();
    for (const mat of Array.isArray(m.material) ? m.material : [m.material]) {
      const tex = ownTextures && (mat as THREE.ShaderMaterial).isShaderMaterial ? (mat as THREE.ShaderMaterial).uniforms.map?.value : null;
      if (tex instanceof THREE.Texture) tex.dispose();
      mat.dispose();
    }
  });
  /** Moves the map-wide meshes' origin to (x, z) without moving what they draw. */
  const rebase = (x: number, z: number) => {
    const dx = anchor.x - x, dz = anchor.y - z;
    anchored.traverse((o) => { if ((o as THREE.Mesh).isMesh && !o.userData.ghost) (o as THREE.Mesh).geometry.translate(dx, 0, dz); });   // ghosts share theirs
    anchor.set(x, z);
    anchored.position.set(x, 0, z);
  };
  /** Frees the previous island's terrain, water, decor, effects, static batch and instanced batches, and empties the scene. */
  const release = () => {
    if (terrain) dispose(terrain);
    if (water) dispose(water.mesh);
    if (decor) dispose(decor);
    if (fx) dispose(fx.group, true);
    if (ground) {
      if (worldUniforms.uGround.value === ground.texture) worldUniforms.uGround.value = neutralGround;   // another scene may own it by now
      if (worldUniforms.uGroundSun.value === ground.sunTexture) worldUniforms.uGroundSun.value = neutralGround;
      ground.dispose();
    }
    staticBatch.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).geometry.dispose(); });
    scene.clear(); entries.clear(); tiles.clear(); pending.clear(); instancer.clear();
    for (const g of [staticSource, staticBatch, dynamic, proxies, anchored]) g.clear();
    terrain = null; water = null; decor = null; fx = null; ground = null; staticGhosts = []; staticFades = [];
    staticDirty = false; groundDirty = false;
  };
  /** Water-surface points along the sea shelf (the fishable sea runs), for the sparkles. */
  const shelfSpots = (m: MapData): THREE.Vector3[] => m.water.filter((a) => a.kind === 'sea')
    .flatMap((a) => a.runs.flatMap(([y, x0, x1]) => Array.from({ length: x1 - x0 }, (_, k) => new THREE.Vector3(x0 + k + 0.5, SEA_LEVEL, y + 0.5))));

  return {
    scene,
    /** A rebuild (a resync) keeps the feature layers: groups from layer() stay valid and are re-added to the new scene. Anything else a
     *  caller added to `scene` (lights) must be added again. */
    async build(m, objects, c) {
      const run = ++epoch;
      map = m; ctx = c; beach = beachHeight(m);
      release();
      anchor.set(0, 0);
      anchored.position.set(0, 0, 0);
      water = buildWater(m);
      fx = createFx();
      terrain = new THREE.Group();
      terrain.name = 'terrain';
      terrain.add(buildTerraces(m), buildBeach(m), buildRamps(m));
      patchObject(terrain);
      patchObject(water.mesh);
      decor = buildDecor(m, c.town.seed);
      ground = new GroundLayer(m, c.town.seed, decor.userData.worn as GroundWorn);   // worn round the stones as decor draws them
      worldUniforms.uGround.value = ground.texture;
      worldUniforms.uGroundXf.value.copy(ground.xform);
      patchObject(decor, { wind: [0.03, 0, 0.3] });
      fx.setFlowers(decor.userData.flowerSpots as THREE.Vector3[]);
      fx.setSparkles(shelfSpots(m));
      anchored.add(terrain, water.mesh, staticBatch);
      scene.add(anchored, decor, fx.group, instancedRoot, staticSource, dynamic, proxies, ...layers.values());
      // Every snapshot object is pending from the start, so an update or remove that arrives before the loop reaches it supersedes it.
      const queued = objects.map((o) => { const t = ++tokens; pending.set(o.id, t); return [o, t] as const; });
      for (const [o, t] of queued) {
        if (pending.get(o.id) !== t) continue;                    // updated (that state is loading) or removed since the snapshot
        await add(o, t);
        if (run !== epoch) return;                                // a newer build took over
      }
      rebatch();
      instancer.flush();
      paintGround();
    },
    async addObject(o) { await add(o); queueMicrotask(rebatch); },
    updateObject(o) {
      if (!ctx) return;
      const e = entries.get(o.id);
      if (!pending.has(o.id)) {                                  // a build in flight is superseded by this newer state below
        if (!e) return;
        // In place only while the footprint holds: a move, turn or catalog change rebuilds, so placeOnTile and `tiles` stay right.
        const same = o.x === e.obj.x && o.y === e.obj.y && o.rot === e.obj.rot && o.catalogId === e.obj.catalogId;
        if (same && e.def.update && viewFor(o) === e.def) {
          e.obj = o; e.def.update(o, e.view, ctx);
          e.view.traverse((x) => { if (x instanceof InstanceProxy) instancer.touch(x); });
          return;
        }
      }
      void add(o).then(() => queueMicrotask(rebatch));
    },
    removeObject(id) { pending.delete(id); remove(id); queueMicrotask(rebatch); },
    layer(name) {
      let g = layers.get(name);
      if (!g) { g = new THREE.Group(); g.name = name; layers.set(name, g); scene.add(g); }
      return g;
    },
    heightAt,
    refreshPerResident(you) {
      if (!ctx) return;
      ctx = { ...ctx, you };
      for (const e of entries.values()) if (e.def.perResident && e.def.update) {
        e.def.update(e.obj, e.view, ctx);
        e.view.traverse((x) => { if (x instanceof InstanceProxy) instancer.touch(x); });
      }
    },
    tick(dt, light) {
      const cam = worldUniforms.uCamXZ.value;
      if (map && Math.hypot(cam.x - anchor.x, cam.y - anchor.y) > REBASE_AT) rebase(Math.round(cam.x), Math.round(cam.y));
      instancer.sun(light.sunDir);
      water?.update(dt, light);
      fx?.update(dt, light);
      if (instancer.isDirty) instancer.flush();
      if (groundDirty) paintGround();
      if (fading) fadeObjects();
      if (ground && worldUniforms.uGround.value === ground.texture) {
        worldUniforms.uGroundLamp.value.copy(lampColour).multiplyScalar(light.lamps * LAMP.intensity);
        // Low quality: the sun's (or the moon's) soft shadows painted on the ground, repainted as it moves; none where shadow maps draw.
        const on = worldUniforms.uGroundSunOn.value > 0;
        if (on) ground.sun(light.sunDir);
        worldUniforms.uGroundSun.value = on ? ground.sunTexture : neutralGround;
        worldUniforms.uGroundSunK.value = on ? Math.min(1, light.sunIntensity / SUN_FULL) : 0;
      }
    },
    picking: { objectAt: (t) => { const id = tiles.get(t.y * 4096 + t.x); return id ? (entries.get(id)?.obj ?? null) : null; } },
    dispose() {
      epoch++;                                                    // a build still loading stops at its next object (and files nothing)
      // Non-instanced, non-batched views (the board) own the meshes they baked (bakeMeshes marks them) and their contact blobs.
      dynamic.traverse((o) => { if ((o as THREE.Mesh).isMesh && (o.userData.own || o.userData.blob)) (o as THREE.Mesh).geometry.dispose(); });
      release();
      layers.clear();
      map = null; ctx = null; beach = null;
    },
  };
}
